import React, {useContext, useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import {Box, CircularProgress, Snackbar} from "@mui/material";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Link as RouterLink,
    Navigate,
    useNavigate, useLocation, useSearchParams, useParams
} from "react-router-dom";
import LoginScreen from './mobile/LoginSreen';
import SignUp from './SignUp';
import NotFound from "./NotFound";
import axios from "axios";
import { DateTime } from 'luxon';
import AppScreen from "./mobile/AppScreen";
import Alert from "@mui/material/Alert";
import AccountScreen from './mobile/AccountScreen';
import ChatScreen from './mobile/ChatScreen';
import AppScreenDesktop from './desktop/AppScreenDesktop';
import { jwtDecode } from "jwt-decode";

export const AuthContext = React.createContext(null);
export const GlobalDataContext = React.createContext(null);

const ProtectedRoute = ({ children }) => {
    // const { id } = useContext(AuthContext);
    const JWT_SESSION = window.localStorage.getItem('JWT_SESSION');

    if (!JWT_SESSION) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

const AdminUserRouteSwitcher = ({ adminRoutes, userRoutes }) => {
    const { authorities } = useContext(AuthContext);

    if (authorities.includes('FINDER_ADMIN')) {
        return adminRoutes;
    }

    return userRoutes;
};

const IsMobile = ({ mobile, desktop }) => {
    const isMobile = () => {
        let check = false;
        (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
        return check;
    };

    if (isMobile()) {
        return mobile;
    }

    return desktop;
}

const AuthProvider = ({ children }) => {

    const EMPTY_USER = {
        id: "",
        userId: "",
        phoneNumber: "",
        userName: "",
        token: "",
    };

    const navigate = useNavigate();

    const [ user, setUser ] = useState(EMPTY_USER)
    const [ notification, setNotification ] = useState({
        isOpen: false,
        severity: "success",
        text: ""
    })

    const { pathname } = useLocation();
    // check if user is logged in
    useEffect(() => {
        async function checkAuthenticatedUser () {
            const jwt_token = window.localStorage.getItem('JWT_SESSION');
            try {
                const resp = await axios.post(`/api/v1/auth/verify`, {
                    jwt: jwt_token
                });

                axios.defaults.headers.common['Authorization'] = `Bearer ${jwt_token}`;
                const decoded = jwtDecode(jwt_token);
                setUser(ps => ({...ps, id: decoded?.sub, userId: decoded?.sub, phoneNumber: decoded?.phoneNumber}))
            } catch (err) {
                window.localStorage.setItem('JWT_SESSION', '');
                navigate("/login")
            }
        }

        checkAuthenticatedUser();
    }, [])

    useEffect(() => {
        const jwt_token = window.localStorage.getItem('JWT_SESSION');
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwt_token}`;
    }, []);

    const handleLogin = async (jwt_token) => {
        delete axios.defaults.headers.common["Authorization"];
        window.localStorage.setItem('JWT_SESSION', jwt_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${jwt_token}`;
        const decoded = jwtDecode(jwt_token);

        if (decoded.exp > new Date().getTime()/1000) {
            setUser(ps => ({...ps, userId: decoded?.sub, phoneNumber: decoded?.phoneNumber}))
        }

        // try {
            // const resp = await axios.post(`/api/token`,
            //     {
            //         username: passedUser? passedUser.email : "",
            //         password: passedUser? passedUser.password : ""
            //     }, {
            //         headers: {
            //             'Content-Type': 'application/json',
            //         }
            //     }
            // );

            // window.localStorage.setItem('JWT_SESSION', resp.data.token);
            // setUser(resp.data);
            // check that use is admin or simple user

            // const userAuthorities = resp.data.authorities;
            // if (userAuthorities.includes('FINDER_ADMIN')) {
            //     navigate('/admin/users');
            // } else {
            //     navigate('/company-lists');
            // }
        // } catch (err) {
            //todo: empty input form when user is incorrect
            // if (err.message.match(/403/) != null) {
            //     setNotification({
            //         isOpen: true,
            //         severity: "warning",
            //         text: "We have sent you a verification email previously. Please check it in your inbox or spam folder."
            //     })
            // } else {
            //     setNotification({
            //         isOpen: true,
            //         severity: "warning",
            //         text: "Неправильный email или пароль"
            //     })
            // }
        // }
    };


    const handleLogout = () => {
        localStorage.setItem('JWT_SESSION', '');
        setUser(EMPTY_USER);
        navigate('/login');
    };

    // if (!user.token && pathname != '/login' && pathname != '/signup' && !/\/users\/\d+\/enable/.test(pathname)) {
    //     return (
    //         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '40%'}}>
    //             <CircularProgress />
    //         </Box>
    //     )
    // }

    return (
        <AuthContext.Provider value={{ ...user,  onLogin: handleLogin, onLogout: handleLogout }}>
            { notification.isOpen ?
                <>
                    <Snackbar
                        open={notification.isOpen}
                        autoHideDuration={6000}
                        onClose={() => { setNotification(prevState => ({...prevState, isOpen: false}))}}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                    >
                        <Alert onClose={() => { setNotification(prevState => ({...prevState, isOpen: false}))}} severity={notification.severity} sx={{ width: '100%' }}>
                            { notification.text }
                        </Alert>
                    </Snackbar>
                </>
                : null
            }
            {children}
        </AuthContext.Provider>
    );
};


const GlobalProvider = ({ children }) => {
    const location = useLocation();
    const isAppScreen = location.pathname == null;

    const [ searchParams, setSearchParams ] = useSearchParams();

    const getInitialService = () => {
        const passedService = searchParams.get('service');

        return isAppScreen ? passedService : null;
    }

    const getInitialDate = () => {
        const passedDate = searchParams.get('date');

        let showedDate = DateTime.fromISO(passedDate, { zone: 'utc'});

        return showedDate.isValid && showedDate > DateTime.now() && isAppScreen ? showedDate : DateTime.now();
    }

    const DEFAULT_SFILTERS =(() => {

        let initialFilters = [ {type: 'date', value: getInitialDate()  }];

        if (getInitialService()) {
            initialFilters.push({type: 'service', value: getInitialService()});
        }

        return initialFilters;
    })();

    const [ gsFilters, setGSFilters ] = useState(DEFAULT_SFILTERS);

    return (
        <GlobalDataContext.Provider value={{gsFilters, setGSFilters}}>
            {children}
        </GlobalDataContext.Provider>
    );
}

// const Route

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
            <Router>
                <IsMobile
                    mobile={(
                        <AuthProvider>
                            <GlobalProvider>
                            <Routes>
                                {/*<Route path="/login" element={<Login />} />*/}
                                {/*<Route path="/signup" element={<SignUp />} />*/}
                                <Route path='/login'
                                       element={
                                           <LoginScreen/>
                                       }
                                />
                                <Route path={'/'}
                                       element={
                                           <ProtectedRoute>
                                               <AppScreen />
                                           </ProtectedRoute>
                                       }
                                />
                                <Route path='/account'
                                       element={
                                           <ProtectedRoute>
                                                <AccountScreen/>
                                           </ProtectedRoute>
                                       }
                                />
                                <Route path='/chats'
                                       element={
                                           <ProtectedRoute>
                                               <ChatScreen/>
                                           </ProtectedRoute>
                                       }
                                />
                            </Routes>
                            </GlobalProvider>
                        </AuthProvider>
                    )}
                    desktop={(
                        <AppScreenDesktop/>
                    )}
                />
            </Router>
    </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
